import "./shipping-tab.scss";

import * as React from "react";

import {cst, cstAssign}                 from "@Common/react/chain-services";
import {consumeContext}                 from "@Common/react/context";
import {Load2}                          from "@Common/react/load2";
import {Types}                          from "@Common/types";
import {US_STATES}                      from "@Common/ui-components/heavy/common/us-states";
import {CA_PROVINCES}                   from "@Common/ui-components/heavy/common/canada-provinces";
import {SearchableSelect}               from "@Common/ui-components/searchable-select/searchable-select";
import {entries, equalDeep, filterKeys} from "@Common/utils/objects";

import {SliderCheckbox} from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {ShippingAddressFormState} from "@Xpresso/routes/common/shipping-address-form/shipping-address-form-state";

import {makeFieldFactory, makeInputFactory} from "../common/tab-fields";

type FormModel = Types.Xpresso.Common.Shipping.AddressForm.Model;
type UsState   = Types.Common.UiComponents.Heavy.Common.UsStates[number];

const FIELD_LABELS:{[key in keyof FormModel]-?: string} = {
    address1     : "Address 1",
    address2     : "Address 2",
    city         : "City",
    company      : "Company",
    contactName  : "Name",
    contactPhone : "Phone",
    countryCode  : "Country",
    plus4        : "+4 Code",
    postalCode   : "Zip",
    state        : "State",
    type         : "Is this a business address?",
}

export const ShippingTab = ({
    branch,
    disabled,
    hasUnsavedChangesStatic : {set: setHasUnsavedChanges},
    onSave                  : emitSave,
    resetCallbackSetter     : setResetCallback,
}:{
    branch                   : Types.Apis.Payloads.Next.Branch.GetById.Response,
    disabled                ?: boolean,
    hasUnsavedChangesStatic  : Types.Common.React.Static2.Static2Next<boolean>,
    onSave                   : (saving:Promise<void>) => void,
    resetCallbackSetter      : (reset:() => void) => void,
}): React.FunctionComponentElement<any> => cst<{
    addressLoad  : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Shipping.GetOfficeShippingAddress.Response>,
    apis         : Types.Apis.Loaders.XpressoLoadApis,
    countryCodes : Types.Xpresso.Loaders.ContextNameMap['countryCodes'],
    formState    : Types.Xpresso.Common.Shipping.AddressForm.StateNext,
    fieldFactory : Types.Xpresso.Account.Admin.OfficeManagement.ManagementModal.TabFieldFactory<FormModel>,
    inputFactory : Types.Xpresso.Account.Admin.OfficeManagement.ManagementModal.TabInputFactory<FormModel>,
}>(
    consumeContext("apis"),
    consumeContext("countryCodes"),
    cstAssign("formState", ({}, next) => ShippingAddressFormState({next})),
    cstAssign("addressLoad", ({apis, formState}, next) => (
        Load2({
            next,
            _key   : branch?.idBranch,
            fetch  : () => branch ? apis.next.shipping.getOfficeShippingAddress(branch) : Promise.resolve(null),
            onLoad : (address) => formState.fromValue(address),
        })
    )),
    ({addressLoad: {value: address}, formState}, next) => next(setResetCallback(() => formState.fromValue(address))),
    cstAssign("inputFactory", ({formState}, next) => next(makeInputFactory({formState, branch, setHasUnsavedChanges}))),
    cstAssign("fieldFactory", ({formState, inputFactory}, next) => next(makeFieldFactory({formState, inputFactory}))),
    ({
        addressLoad  : {loading: loadingAddress, reload: reloadAddress, value: loadedAddress},
        apis,
        countryCodes,
        formState,
        fieldFactory : makeField,
        inputFactory : makeInput,
    }) => (
        <div className="shipping-tab-45f" xd-qa-type="card" xd-qa-name="Shipping Info">
            <header xd-qa-type="label" xd-qa-name="Office Shipping Info">Office Shipping Info</header>
            <section>
                <div className="checkbox field-row">
                    <label xd-qa-type="label" xd-qa-name={FIELD_LABELS.type}>
                        {FIELD_LABELS.type}
                        <SliderCheckbox disabled={disabled || loadingAddress} value={formState.value.type === "office"} xdQaName={formState.value.type === "office" ? "Business Address Slider true" : "Business Address Slider false"}/>
                        <input
                            type="checkbox"
                            hidden
                            checked={formState.value.type === "office"}
                            disabled={disabled || loadingAddress}
                            onChange={(e) => {
                                setHasUnsavedChanges(true);
                                formState.field("type").onChange(e.target.checked ? "office" : "residential");
                            }}
                        />
                    </label>
                </div>

                {Array.from([
                    "contactName",
                    "company",
                    "address1",
                    "address2",
                    "city",
                ] as const).map((path:keyof FormModel & `branchDisplay${string}`) => (
                    makeField({path, disabled: disabled || loadingAddress, label: FIELD_LABELS[path], xdQaName: ""})
                ))}

                <div className="field-row">
                    <label xd-qa-type="label" xd-qa-name={`${FIELD_LABELS.state} / ${FIELD_LABELS.postalCode}`}>{FIELD_LABELS.state} / {FIELD_LABELS.postalCode}</label>
                    <div className="double input-column">
                        {formState.value.countryCode === "US" || formState.value.countryCode === "CA" ? (
                            <SearchableSelect<UsState>
                                disabled={disabled || loadingAddress}
                                getLabel={(state) => state.abbreviation}
                                isSelected={(state) => formState.value.state === state.abbreviation}
                                list={formState.value.countryCode === "US" ? US_STATES : CA_PROVINCES}
                                onSelect={(state) => {
                                    setHasUnsavedChanges(true);
                                    formState.field("state").onChange(state.abbreviation);
                                }}
                                placeholder="State"
                                xdQaName={FIELD_LABELS.state}
                            />
                        ) : (
                            makeInput({disabled: disabled || loadingAddress, label: FIELD_LABELS.state, path: "state", xdQaName: ""})
                        )}
                        {makeInput({disabled: disabled || loadingAddress, label: FIELD_LABELS.postalCode, path: "postalCode", xdQaName: ""})}
                    </div>
                </div>

                <div className="field-row">
                    <label xd-qa-type="label" xd-qa-name={FIELD_LABELS.countryCode}>{FIELD_LABELS.countryCode}</label>
                    <SearchableSelect<{label: string, value: string}>
                        disabled={disabled || loadingAddress}
                        getLabel={(code) => code.label}
                        isSelected={(code) => formState.value.countryCode === code.value}
                        list={entries(countryCodes ?? {}).map(([value, label]) => ({label, value}))}
                        onSelect={(code) => {
                            setHasUnsavedChanges(true);
                            formState.field("countryCode").onChange(code.value);
                        }}
                        placeholder="Country"
                        xdQaName={FIELD_LABELS.countryCode}
                    />
                </div>

                {makeField({disabled: disabled || loadingAddress, label: FIELD_LABELS.contactPhone, path: "contactPhone", xdQaName: ""})}
            </section>

            <button
                disabled={disabled || loadingAddress || equalDeep(formState.value, filterKeys(loadedAddress ?? {}, (key) => key in formState.value)) || !formState.validate()}
                onClick={() => {
                    const id     = loadedAddress?.idPrimary;
                    const saving = (
                        id
                            ? apis.next.shipping.updateShippingAddress({id, address: formState.value})
                            : apis.next.shipping.addShippingAddress({address: formState.value, idEntity: branch.idBranch})
                    );
                    saving.then(reloadAddress);
                    emitSave(saving);
                }}
                xd-qa-type="button"
                xd-qa-name="Save"
            >
                Save
            </button>
        </div>
    )
);

